import React, {
	type ReactNode,
	createContext,
	useContext,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { debounce } from './utils.tsx';

type IssueId = number;

type Subscriber = (ids: IssueId[]) => void;

type ContextType = {
	subscribe: (subscriber: Subscriber) => void;
	unsubscribe: (subscriber: Subscriber) => void;
	notify: (issueId: IssueId) => void;
} | null;

type ProviderProps = {
	children: ReactNode;
};

const DEBOUNCE_DELAY = 100;

const IssueEventContext = createContext<ContextType>(null);

export const IssueEventProvider = ({ children }: ProviderProps) => {
	const [subscribers, setSubscribers] = useState<Subscriber[]>([]);

	const subscribe = useCallback((subscriber: Subscriber) => {
		setSubscribers((prevSubscribers) => [...prevSubscribers, subscriber]);
	}, []);

	const unsubscribe = useCallback((subscriber: Subscriber) => {
		setSubscribers((prevSubscribers) => prevSubscribers.filter((s) => s !== subscriber));
	}, []);

	const notify = useMemo(
		() =>
			debounce((ids: IssueId[]) => {
				subscribers.forEach((subscriber) => subscriber(ids));
			}, DEBOUNCE_DELAY),
		[subscribers],
	);

	const value = useMemo(
		() => ({ subscribe, unsubscribe, notify }),
		[subscribe, unsubscribe, notify],
	);

	return <IssueEventContext.Provider value={value}>{children}</IssueEventContext.Provider>;
};

export const useIssueEvents = (onChange?: (ids: IssueId[]) => void) => {
	const { subscribe, unsubscribe, notify } = useContext(IssueEventContext) ?? {};

	useEffect(() => {
		if (!onChange || !subscribe || !unsubscribe) {
			return undefined;
		}

		subscribe(onChange);

		return () => unsubscribe(onChange);
	}, [subscribe, unsubscribe, onChange]);

	return notify;
};
